/**
 * Knowledge API (owner: C1): odor vocabulary tree and terms, regulatory datasets, reference lists with
 * coverage, and usage ranges under /api/kb/ (docs/mvp-build-spec.md §5). Query keys start with
 * "knowledge". The materials feature reuses `useOdorTree` for its family filter.
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPost, isApiError } from "@/api/client";
import type { DecimalString, IsoDate, IsoDateTime, Paged, Uuid } from "@/api/types";
import type { BestPrice, MaterialListItem, OdorTermRef, SourceRef, UsageStat } from "@/features/materials/api";

export interface OdorTreeNode extends OdorTermRef {
  definition: string;
  scentree_id: string | null;
  material_count: number;
  children: OdorTreeNode[];
}

export interface OdorTree {
  families: OdorTreeNode[];
  /** Descriptors that no known odor path places under a family yet. */
  unplaced: OdorTreeNode[];
}

export interface OdorTermDetail extends OdorTermRef {
  definition: string;
  scentree_id: string | null;
  path: OdorTermRef[];
  aliases: Array<{ text: string; source: SourceRef | null }>;
  children: OdorTermRef[];
  material_count: number;
}

export interface Dataset {
  id: Uuid;
  publisher: string;
  title: string;
  version_label: string;
  kind: "ifra_standards" | "ifra_ncs_annex" | "eu_allergens" | "eu_caps" | "other";
  status: "proposed" | "notified" | "in_force" | "superseded" | "what_if";
  published_on: IsoDate | null;
  effective_new: IsoDate | null;
  effective_existing: IsoDate | null;
  source_url: string;
  notes: string;
  rule_count: number;
  source: SourceRef | null;
}

export interface CoverageTotals {
  total: number;
  owned: number;
  owned_variant: number;
  missing: number;
  unresolved: number;
  /** Owned or covered by a variant, out of the resolved items; null when nothing resolved. */
  covered_fraction: DecimalString | null;
}

export interface ReferenceList {
  id: Uuid;
  name: string;
  source: string;
  description: string;
  is_private: boolean;
  created_at: IsoDateTime;
}

export interface ReferenceListSummary extends ReferenceList {
  item_count: number;
  totals: CoverageTotals;
}

export type CoverageStatus = "owned" | "owned_variant" | "missing" | "unresolved";

export interface CoverageItem {
  id: Uuid;
  position: number;
  raw_name: string;
  section: string;
  material: { id: Uuid; name: string; kind: string } | null;
  status: CoverageStatus;
  reason: "same_material" | "same_substance" | "same_parent_substance" | "same_structure" | "same_species" | null;
  matched: Array<{ id: Uuid; name: string }>;
  best_price: BestPrice | null;
}

export interface Coverage {
  reference_list: ReferenceList;
  items: CoverageItem[];
  totals: CoverageTotals;
}

export interface ReferenceListCreated {
  reference_list: ReferenceList;
  item_count: number;
  resolved_count: number;
  unresolved: Array<{ position: number; raw_name: string }>;
  ambiguous: Array<{
    position: number;
    raw_name: string;
    chosen: { id: Uuid; name: string };
    others: Array<{ id: Uuid; name: string }>;
  }>;
}

export type UsageSource = "demo_formulas" | "patents" | "workspace";
export type UsageSort = "-n" | "name" | "-median" | "median";

export interface UsageParams {
  q?: string;
  source_kind?: UsageSource;
  sort?: UsageSort;
  limit?: number;
  offset?: number;
}

/** Body of POST /api/purchasing/baskets (purchasing slice, C2). */
export interface BasketDraft {
  name: string;
  lines: Array<{ material_id: Uuid; neat_g: DecimalString }>;
}

export const knowledgeKeys = {
  all: ["knowledge"] as const,
  tree: ["knowledge", "odor-tree"] as const,
  term: (id: Uuid) => ["knowledge", "odor-term", id] as const,
  termMaterials: (id: Uuid, offset: number) => ["knowledge", "odor-term-materials", id, offset] as const,
  datasets: ["knowledge", "datasets"] as const,
  lists: ["knowledge", "reference-lists"] as const,
  coverage: (id: Uuid) => ["knowledge", "coverage", id] as const,
  usage: (params: UsageParams) => ["knowledge", "usage", params] as const,
};

export function useOdorTree() {
  return useQuery({
    queryKey: knowledgeKeys.tree,
    queryFn: ({ signal }) => apiGet<OdorTree>("/kb/odor-terms/tree", { signal }),
    staleTime: 5 * 60_000,
  });
}

export function useOdorTerm(id: Uuid | null) {
  return useQuery({
    queryKey: knowledgeKeys.term(id ?? ""),
    queryFn: ({ signal }) => apiGet<OdorTermDetail>(`/kb/odor-terms/${id}`, { signal }),
    enabled: Boolean(id),
  });
}

export function useOdorTermMaterials(id: Uuid | null, offset = 0) {
  return useQuery({
    queryKey: knowledgeKeys.termMaterials(id ?? "", offset),
    queryFn: ({ signal }) =>
      apiGet<Paged<MaterialListItem>>(`/kb/odor-terms/${id}/materials`, { params: { limit: 50, offset }, signal }),
    enabled: Boolean(id),
    placeholderData: keepPreviousData,
  });
}

export function useDatasets() {
  return useQuery({
    queryKey: knowledgeKeys.datasets,
    queryFn: ({ signal }) => apiGet<Dataset[]>("/kb/datasets", { signal }),
    staleTime: 5 * 60_000,
  });
}

export function useReferenceLists() {
  return useQuery({
    queryKey: knowledgeKeys.lists,
    queryFn: ({ signal }) => apiGet<ReferenceListSummary[]>("/kb/reference-lists/", { signal }),
  });
}

export function useCoverage(id: Uuid | null) {
  return useQuery({
    queryKey: knowledgeKeys.coverage(id ?? ""),
    queryFn: ({ signal }) => apiGet<Coverage>(`/kb/reference-lists/${id}/coverage`, { signal }),
    enabled: Boolean(id),
  });
}

export function useCreateReferenceList() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; text: string; description?: string }) =>
      apiPost<ReferenceListCreated>("/kb/reference-lists/", body),
    onSettled: () => queryClient.invalidateQueries({ queryKey: knowledgeKeys.lists }),
  });
}

export function useDeleteReferenceList() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: Uuid) => apiDelete(`/kb/reference-lists/${id}`),
    onSettled: () => queryClient.invalidateQueries({ queryKey: knowledgeKeys.lists }),
  });
}

export function useUsageStats(params: UsageParams) {
  return useQuery({
    queryKey: knowledgeKeys.usage(params),
    queryFn: ({ signal }) => apiGet<Paged<UsageStat>>("/kb/usage-stats/", { params: { ...params }, signal }),
    placeholderData: keepPreviousData,
  });
}

/** Queues the `kb.usage_stats` job through the jobs API (progress shows on /jobs). */
export function useRecomputeUsage() {
  return useMutation({
    mutationFn: () => apiPost<{ id: Uuid; status: string }>("/jobs/kb.usage_stats", { payload: {} }),
  });
}

/** Creates a draft basket in purchasing (C2's endpoint) from coverage gaps; tolerates either slash style. */
export async function createBasket(draft: BasketDraft): Promise<{ id?: Uuid; name?: string }> {
  try {
    return await apiPost<{ id?: Uuid; name?: string }>("/purchasing/baskets", draft);
  } catch (error) {
    if (isApiError(error, 404)) return apiPost<{ id?: Uuid; name?: string }>("/purchasing/baskets/", draft);
    throw error;
  }
}

export function useCreateBasket() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createBasket,
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["purchasing"] }),
  });
}
